import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { moduleRoutes, isPathAllowed } from '../lib/moduleAccess';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { LogOut, Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import { QaAlertWatcher } from './quality/QaAlertWatcher';

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-close the mobile drawer whenever the user navigates to a new route.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const allowedRoutes = moduleRoutes(user);
  if (!isPathAllowed(user, location.pathname)) {
    return <Navigate to={allowedRoutes[0].path} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Sidebar — drawer on mobile, persistent on md+ */}
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center flex-shrink-0 gap-2">
          {/* Hamburger — mobile only */}
          <Button
            onClick={() => setMobileOpen(true)}
            variant="ghost"
            size="sm"
            className="md:hidden h-9 w-9 p-0 flex-shrink-0"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Chivo' }}>{user?.company_name || 'Dashboard'}</h2>
            <p className="text-[11px] sm:text-sm text-slate-600 truncate">
              {user?.username}
              {user?.role === 'admin' && <span className="ml-2 text-[10px] sm:text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">Admin</span>}
              {user?.role === 'master' && <span className="ml-2 text-[10px] sm:text-xs bg-slate-800 text-white px-1.5 py-0.5 rounded font-bold uppercase">Master</span>}
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="h-9 sm:h-10 px-2 sm:px-4 border-slate-300 flex-shrink-0"
            data-testid="logout-button"
          >
            <LogOut className="sm:mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </header>

        <main className="flex-1 overflow-auto p-3 sm:p-6">
          <QaAlertWatcher />
          <Outlet />
          <div className="text-center py-2 text-[9px] text-slate-300 select-none pointer-events-none" style={{ userSelect: 'none' }}>
            Swami Software OEE. All rights reserved. Unauthorized copying, redistribution, or reverse engineering is strictly prohibited and subject to legal action.
          </div>
        </main>
      </div>
    </div>
  );
}
